import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { environment } from '../../environments/environment';

export interface AuthPayload {
  idToken: string;
  userPoolId: string;
  identityPoolId: string;
  region: string;
}

@Injectable({ providedIn: 'root' })
export class AuthTokenService implements OnDestroy {
  private payload: AuthPayload | null = null;
  private waiters: Array<(p: AuthPayload) => void> = [];
  private readonly allowedOrigins: string[] = (environment as any).PARENT_ORIGINS || [];
  private readonly messageListener = (event: MessageEvent) => this.onMessage(event);

  constructor(private zone: NgZone) {
    window.addEventListener('message', this.messageListener);
    this.requestFromParent();
  }

  ngOnDestroy(): void {
    window.removeEventListener('message', this.messageListener);
  }

  getPayload(): AuthPayload | null {
    return this.payload;
  }

  waitForPayload(): Promise<AuthPayload> {
    if (this.payload) return Promise.resolve(this.payload);
    this.requestFromParent();
    return new Promise<AuthPayload>(resolve => this.waiters.push(resolve));
  }

  invalidate(): void {
    this.payload = null;
    this.requestFromParent();
  }

  private requestFromParent(): void {
    if (window.parent && window.parent !== window) {
      window.parent.postMessage({ type: 'zeus:auth-request' }, '*');
    }
  }

  private onMessage(event: MessageEvent) {
    if (!this.allowedOrigins.includes(event.origin)) return;
    const data = event.data;
    if (!data || data.type !== 'zeus:auth' || !data.idToken) return;

    const payload: AuthPayload = {
      idToken: data.idToken,
      userPoolId: data.userPoolId,
      identityPoolId: data.identityPoolId,
      region: data.region || 'us-east-1'
    };

    this.zone.run(() => {
      this.payload = payload;
      const pending = this.waiters.splice(0);
      pending.forEach(resolve => resolve(payload));
    });
  }
}
